import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

export function CookieConsent() {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasConsent = localStorage.getItem("cookieConsent");
    if (!hasConsent) {
      // Delay showing the banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem("cookieConsent", "rejected");
    setIsVisible(false);
  };

  const handleCustomise = () => {
    // For now, treat customise as accepting essential only
    localStorage.setItem("cookieConsent", "customised");
    setIsVisible(false);
  };

  const content: Record<string, {
    title: string;
    description: string;
    cookieNotice: string;
    customise: string;
    rejectAll: string;
    acceptAll: string;
  }> = {
    en: {
      title: "We value your privacy.",
      description: "We use cookies to offer you a better experience, analyze site traffic, and serve relevant ads. By clicking Accept All, you consent to the use of cookies in accordance with our",
      cookieNotice: "cookie notice",
      customise: "Customise",
      rejectAll: "Reject All",
      acceptAll: "Accept All",
    },
    zh: {
      title: "我们重视您的隐私。",
      description: "我们使用Cookie为您提供更好的体验、分析网站流量并提供相关广告。点击全部接受即表示您同意根据我们的",
      cookieNotice: "Cookie政策",
      customise: "自定义",
      rejectAll: "全部拒绝",
      acceptAll: "全部接受",
    },
    th: {
      title: "เราให้ความสำคัญกับความเป็นส่วนตัวของคุณ",
      description: "เราใช้คุกกี้เพื่อมอบประสบการณ์ที่ดีขึ้น วิเคราะห์การเข้าชมเว็บไซต์ และแสดงโฆษณาที่เกี่ยวข้อง เมื่อคลิกยอมรับทั้งหมด คุณยินยอมให้ใช้คุกกี้ตาม",
      cookieNotice: "ประกาศเกี่ยวกับคุกกี้",
      customise: "ตั้งค่า",
      rejectAll: "ปฏิเสธทั้งหมด",
      acceptAll: "ยอมรับทั้งหมด",
    },
  };

  const t = content[language] ?? content['en']!;
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-4 left-4 right-4 md:left-4 md:right-auto md:max-w-2xl z-50"
        >
          <div className="bg-white rounded-xl shadow-xl px-6 py-4 border border-gray-100 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-sm font-bold text-gray-900 mb-1">
                {t.title}
              </h2>
              <p className="text-gray-600 text-xs leading-relaxed">
                {t.description}{" "}
                <a href="#" className="text-primary font-medium underline hover:text-primary/80 transition-colors">
                  {t.cookieNotice}
                </a>
                .
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={handleCustomise}
                className="h-8 px-4 text-xs font-medium border-primary text-primary hover:bg-primary/5"
              >
                {t.customise}
              </Button>
              <Button
                onClick={handleRejectAll}
                className="h-8 px-4 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t.rejectAll}
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="h-8 px-4 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t.acceptAll}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
